import { ReactComponent as RemoveIcon } from '@assets/svg/calender_menu_delete.svg';
import { ReactComponent as Close } from '@assets/svg/calender_menu_x.svg';
import CalendarIcon from '@assets/svg/e-calendar/calendar-icon.svg';
import { IMenuRenderInfo } from '@components/ui/calendar/calendars';
import { showNotification } from '@helpers/notification-helper';
import LoadingIcon from '@hilbi/web-common/assets/svg/icon-loading_spinner-dark_purple.svg';
import Input from '@hilbi/web-common/components/e-visits/input';
import { HilbiForm } from '@hilbi/web-common/components/hilbi-form';
import { actions } from '@store/ducks/reservation-form-slice';
import { Button, Form } from '@ui';
import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { ReservationModes } from 'types/reservation-form';
import TStore from 'types/store';
import * as Yup from 'yup';
import * as ReservationFields from './reservation-form-fields';
import { HeaderStyledSC, ReservationFormStyledSC } from './reservation-form.styled';
import { useReservationFormMethods } from './reservation-form.utils';

type ReservationProps = {
  mode: ReservationModes;
  placement?: 'page' | 'modal';
  onClose: () => void;
  handleDeleteClick: () => void;
  direction?: IMenuRenderInfo<any>['direction'];
} & ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const VALIDATION_SCHEMA = Yup.object({
  selectedPatient: Yup.object().nullable(),
  title: Yup.string()
    .max(255, 'reservationForm_title_length')
    .when('selectedPatient', (selectedPatient) => {
      if (!selectedPatient) {
        return Yup.string().required('title_or_patient');
      }

      return Yup.string().nullable();
    }),

  description: Yup.string().max(9999, 'reservationForm_description_length'),
});

const ReservationForm: React.FunctionComponent<ReservationProps> = ({
  mode,
  onClose,
  handleDeleteClick,
  direction,
  reservationForm,
  setFormState,
  placement = 'modal',
}) => {
  const { formatMessage } = useIntl();
  const { handleSubmit, onMount, onUnMount } = useReservationFormMethods(mode);

  useEffect(() => {
    onMount();
    return () => {
      onUnMount();
    };
  }, []);

  const [form, formOptions] = Form.useForm({
    validationSchema: VALIDATION_SCHEMA,
    initialValues: reservationForm.initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (reservationForm.formState === 'submit_success') {
      showNotification({
        type: 'SUCCESS',
        title: formatMessage({
          id:
            mode === 'edit' ? 'reservation_form_update_success' : 'reservation_form_create_success',
        }),
        copy: '',
      });
      setFormState('idle');
      form?.resetForm?.({ values: (form?.values ?? reservationForm.initialValues) as any });
    }
  }, [formatMessage, reservationForm.formState, mode]);

  useEffect(() => {
    if (reservationForm.errorMessage) {
      showNotification({
        type: 'ERROR',
        title: reservationForm.errorMessageTranslated
          ? formatMessage({ id: reservationForm.errorMessage })
          : reservationForm.errorMessage,
        copy: '',
      });
      setFormState('idle');
    }
  }, [reservationForm.errorMessageTranslated, reservationForm.errorMessage]);

  const selectedPatient = React.useMemo(
    () =>
      // @ts-expect-error structure change
      reservationForm.initialValues.extraData?.clientId
        ? {
            // @ts-expect-error ehe
            value: reservationForm.initialValues.extraData.clientId,
            // @ts-expect-error ehe
            label: `${reservationForm.initialValues.extraData.client.firstName} ${reservationForm.initialValues.extraData.client.lastName}`,
          }
        : {
            value: reservationForm.initialValues.title,
            label: reservationForm.initialValues.title,
          },
    // @ts-expect-error structure change
    [reservationForm.initialValues.extraData],
  );

  useEffect(() => {
    form?.resetForm?.({
      values: {
        ...form?.values,
        timeRangePickerValue: reservationForm.timeRangePickerValue,
        // @ts-expect-error
        selectedPatient,
      },
    });
  }, [reservationForm.timeRangePickerValue, selectedPatient]);

  const headerActions: any = useMemo(() => {
    const actions = [
      {
        icon: <Close />,
        onClick: onClose,
      },
    ];

    if (mode === 'edit') {
      actions.unshift({
        icon: <RemoveIcon />,
        onClick: handleDeleteClick,
      });
    }

    return actions.map((action, i) => {
      return (
        <button key={i} className="action" type="button" onClick={action.onClick}>
          {React.cloneElement(action.icon)}
        </button>
      );
    });
  }, []);

  if (reservationForm.formState === 'initializing') {
    return (
      <ReservationFormStyledSC mode={mode}>
        <HeaderStyledSC mode={mode}>
          <Input name={'title'} className={'title-input'} variation={'lined'} />
          <div className="actions">{headerActions}</div>
        </HeaderStyledSC>
        <div className={'loading-indicator'}>
          <img src={LoadingIcon} alt="patient" width="40" height="40" />
        </div>
      </ReservationFormStyledSC>
    );
  }

  return (
    <Form {...formOptions}>
      <ReservationFormStyledSC
        mode={mode}
        className={classNames('event-edit-form', `event-edit-form-${placement}`)}
      >
        <HilbiForm variation={'lined'}>
          <HeaderStyledSC mode={mode} direction={direction}>
            <label className={'patient-select-label'}>
              {formatMessage({
                id: 'reservation_form_add_patient_name',
              })}
            </label>

            <ReservationFields.PatientSelect name="selectedPatient" />
            {placement === 'modal' ? <div className="actions">{headerActions}</div> : null}
          </HeaderStyledSC>
          <>
            <div className={'time-range-fields'}>
              {mode === 'edit' ? <ReservationFields.DateField name={'dateField'} /> : null}
              {mode === 'edit' ? null : (
                <div className={'date-field'}>
                  <img src={CalendarIcon} alt={'clock'} width="40" height="40" />
                  <span>{reservationForm.displayedDate}</span>
                </div>
              )}
              {mode === 'edit' ? (
                <ReservationFields.TimeRangeSelect name={'timeRangeSelectValue'} />
              ) : null}
              {reservationForm.timeRangePickerHidden ? null : (
                <ReservationFields.TimeRangePickerField
                  name={'timeRangePickerValue'}
                  disabled={mode === 'add'}
                />
              )}
            </div>
            {mode === 'add' ? null : (
              <ReservationFields.ReservationTagSelect name={'selectedTag'} />
            )}
            <ReservationFields.TextAreaField
              name={'description'}
              placeholder={formatMessage({ id: 'reservation_form_text_field_placeholder' })}
            />
            <Form.Custom>
              {(ff) =>
                mode === 'edit' && ff.values.patientDescription ? (
                  <ReservationFields.TextAreaField name={'patientDescription'} disabled={true} />
                ) : null
              }
            </Form.Custom>
            <Button
              className={'submit-button'}
              type="submit"
              size={placement === 'modal' ? 'small' : 'large'}
              loading={reservationForm.formState === 'submitting'}
            >
              <FormattedMessage id="addForm_submit_action" />
            </Button>
          </>
        </HilbiForm>
      </ReservationFormStyledSC>
      {/* Broken for now..., Wrong initialValues */}
      <Form.Custom>
        {(ff) => (
          <Prompt
            when={mode === 'edit' && ff.dirty}
            message={formatMessage({ id: 'reservation_form_not_saved' })}
          />
        )}
      </Form.Custom>
    </Form>
  );
};

const mapStateToProps = (state: TStore) => ({
  reservationForm: state.reservationForm,
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    ...bindActionCreators(
      {
        changeReservationFormDate: actions.changeReservationFormDate,
        setFormState: actions.setFormState,
      },
      dispatch,
    ),
    resetError: () => {
      dispatch(actions.setErrorMessage({ message: '', translated: false }));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(ReservationForm));
